import { eventChannel } from 'redux-saga';
import _forEach from 'lodash/forEach';
import { createSignalingClient, createPeerConnection, getUserMediaConstraints, getUserMedia } from './utils';

import { getLogger } from '../../logger/index';

const logger = getLogger('communicationVideo :: WebRTC');

let signalingClient = null;
let peerConnection = null;
let remoteStreams = [];
const peerConnectionByClientId = {};
const senderMapping = {};
let localStream = null;

export function initLocalStream(type, mediaDevices) {
  return getUserMedia(mediaDevices, type, {});
}

export function setRTCLocalStream(stream) {
  logger.debug('setRTCLocalStream', stream);
  localStream = stream;
}

export async function exchangeLocalStream(type, oldStream, mediaDevices) {
  logger.debug('Add video to stream', oldStream);

  const newStream = await initLocalStream(type, mediaDevices);
  logger.debug('New video stream', newStream);

  logger.debug('Stop prev localStream', oldStream);
  stopStream(oldStream);

  connectStreamToPeerConnection(newStream, peerConnection);
  localStream = newStream;

  return newStream;
}

export function peerNegotiate(iceRestart = false) {
  return peerConnection
    .createOffer({
      iceRestart,
      offerToReceiveAudio: true,
      offerToReceiveVideo: true,
    })
    .then(offer => peerConnection.setLocalDescription(offer))
    .then(() => {
      if (signalingClient) {
        signalingClient.sendSdpOffer(peerConnection.localDescription);
      } else {
        logger.error('No signalingClient');
      }
    })
    .catch(e => logger.error('createOffer / sendSdpOffer error 3', e));
}

export function createMasterConnectionChannel({ settings, RTCPeerConnection, type, mediaDevices }) {
  return eventChannel(emit => {
    try {
      signalingClient = createSignalingClient(settings, { isMaster: true });

      // Once the signaling channel connection is open, connect to the webcam and create an offer to send to the master
      signalingClient.on('open', () => {
        logger.debug('[MASTER] Connected to signaling service');
        emit({ eventName: 'statusUpdate', args: { source: 'presenter', info: 'initiating' } });
      });

      signalingClient.on('sdpOffer', (offer, remoteClientId) => {
        let isRenegotiation = false;
        logger.debug('[MASTER] Received SDP offer from client: ' + remoteClientId, offer);
        if (!remoteClientId) {
          logger.warn('No remoteClientId in sdpOffer');
          return;
        }
        if (!peerConnection || !peerConnectionByClientId[remoteClientId]) {
          peerConnection = createPeerConnection(settings, RTCPeerConnection);
          peerConnectionByClientId[remoteClientId] = peerConnection;

          // Send any ICE candidates to the other peer
          peerConnection.addEventListener('icecandidate', ({ candidate }) => {
            if (candidate) {
              if (signalingClient) {
                logger.debug('[MASTER] Generated ICE candidate for client: ' + remoteClientId, candidate);
                signalingClient.sendIceCandidate(candidate, remoteClientId);
              } else {
                logger.error('No signalingClient (icecandidate)');
              }
            } else {
              logger.debug('[MASTER] All ICE candidates have been generated for client: ' + remoteClientId);
            }
          });

          peerConnection.addEventListener('iceconnectionstatechange', args => {
            logger.debug('iceconnectionstatechange presenter', args);
            emit({
              eventName: 'statusUpdate',
              args: { source: 'presenter', info: peerConnection ? peerConnection.iceConnectionState : 'closed' },
            });
          });
          peerConnection.addEventListener('connectionstatechange', args => {
            logger.debug('connectionstatechange presenter', args);
            emit({
              eventName: 'statusUpdate',
              args: { source: 'presenter', info: peerConnection ? peerConnection.iceConnectionState : 'closed' },
            });
          });
          peerConnection.addEventListener('signalingstatechange', event => {
            logger.debug('signalingstatechange', event);
          });
          peerConnection.addEventListener('negotiationneeded', event => {
            logger.debug('negotiationneeded master', event);
          });
          peerConnection.addEventListener('icegatheringstatechange', event => {
            logger.debug('icegatheringstatechange', event);
          });

          // remoteStreams = [];
          onRemoteStream(peerConnection, stream => {
            logger.debug('[MASTER] Received remote stream from client: ' + remoteClientId, stream);
            remoteStreams.push(stream);
            emit({ eventName: 'remoteStream', args: stream });
          });
        } else {
          isRenegotiation = true;
          logger.debug('[MASTER] peerConnection already exists on sdpOffer (renegotiation)', offer, remoteClientId);
        }

        logger.debug('setRemoteDescription', offer);
        peerConnection
          .setRemoteDescription(offer)
          .then(() => {
            if (!isRenegotiation) {
              logger.debug('initLocalStream 2', type, mediaDevices);
              return initLocalStream(type, mediaDevices)
                .then(stream => {
                  localStream = stream;
                  emit({ eventName: 'localStream', args: localStream });

                  return localStream;
                })
                .then(() => connectStreamToPeerConnection(localStream, peerConnection));
            }
          })
          .then(() => {
            // Create an SDP answer to send back to the client
            logger.debug('[MASTER] Creating SDP answer for client: ' + remoteClientId, 'Type', type);
            return peerConnection.createAnswer({
              offerToReceiveAudio: true,
              offerToReceiveVideo: true, // type === 'video',
            });
          })
          .then(answer => peerConnection.setLocalDescription(answer))
          .then(() => {
            if (signalingClient) {
              logger.debug('[MASTER] Generating ICE candidates for client: ' + remoteClientId);
              return signalingClient.sendSdpAnswer(peerConnection.localDescription, remoteClientId);
            } else {
              logger.error('No signalingClient (icecandidate)');
            }
          })
          .catch(e => logger.error('createOffer / sendSdpOffer error 1', e));
      });

      signalingClient.on('close', () => {
        logger.debug('signalingClient close');
        if (localStream) {
          _forEach(localStream.getTracks(), track => track.stop());
        }
        remoteStreams.forEach(remoteStream => _forEach(remoteStream.getTracks(), track => track.stop()));

        remoteStreams = [];
        signalingClient = null;
        peerConnection = null;
        emit({ eventName: 'statusUpdate', args: { source: 'presenter', info: 'disconnected' } });
        emit({ eventName: 'close' });
      });

      signalingClient.on('error', error => {
        logger.error('signalingClient error', error);
        emit({ eventName: 'error' });
      });

      signalingClient.on('iceCandidate', (candidate, remoteClientId) => {
        logger.debug('[MASTER] Received ICE candidate from client: ' + remoteClientId, candidate);
        // Add the ICE candidate received from the client to the peer connection
        const peerConnection = peerConnectionByClientId[remoteClientId];
        peerConnection.addIceCandidate(candidate);
      });

      signalingClient.open();
    } catch (e) {
      logger.error('Connection error', e);
      closeConnection(localStream);
    }

    return () => {
      closeConnection(localStream);
    };
  });
}

export function createSlaveConnectionChannel({ settings, RTCPeerConnection, type, mediaDevices }) {
  return eventChannel(emit => {
    try {
      peerConnection = createPeerConnection(settings, RTCPeerConnection);
      signalingClient = createSignalingClient(settings, { isMaster: false });

      // Once the signaling channel connection is open, connect to the webcam and create an offer to send to the master
      signalingClient.on('open', async () => {
        // add localstream to the peer connection, and display it in the local view
        try {
          logger.debug('Signaling client open');
          localStream = await initLocalStream(type, mediaDevices);
          emit({ eventName: 'localStream', args: localStream });
          connectStreamToPeerConnection(localStream, peerConnection);

          emit({ eventName: 'open' });
          emit({ eventName: 'statusUpdate', args: { source: 'viewer', info: 'initiating' } });
        } catch (e) {
          // Could not find webcam
          logger.error('connectStreamToPeerConnection error', e);
          return;
        }
        // Create an SDP offer and send it to the master
        peerNegotiate();
      });

      // When the SDP answer is received back from the master, add it to the peer connection.
      signalingClient.on('sdpAnswer', answer => {
        return peerConnection.setRemoteDescription(answer).catch(e => {
          logger.warn(e);
          logger.debug('Try to negotiate connection again');
          peerNegotiate(true);
        });
      });

      // When an ICE candidate is received from the master, add it to the peer connection.
      signalingClient.on('iceCandidate', candidate => {
        logger.debug('[SLAVE] Received icecandidate', candidate);
        peerConnection.addIceCandidate(candidate);
      });

      signalingClient.on('close', () => {
        logger.debug('signalingClient close');
        if (localStream) {
          _forEach(localStream.getTracks(), track => track.stop());
        }
        remoteStreams.forEach(remoteStream => _forEach(remoteStream.getTracks(), track => track.stop()));

        remoteStreams = [];
        signalingClient = null;
        peerConnection = null;
        emit({ eventName: 'close' });
      });

      signalingClient.on('error', error => {
        logger.error('signalingClient error', error);
        emit({ eventName: 'error' });
      });

      // Send any ICE candidates generated by the peer connection to the other peer
      peerConnection.addEventListener('icecandidate', ({ candidate }) => {
        if (candidate) {
          if (signalingClient) {
            logger.debug('[SLAVE] send icecandidate', candidate);
            signalingClient.sendIceCandidate(candidate);
          } else {
            logger.error('No signalingClient (icecandidate)');
          }
        } else {
          // No more ICE candidates will be generated
          logger.debug('[SLAVE] icecandidate - no more ICE candidates will be generated');
        }
      });

      // As remote tracks are received, add them to the remote view
      onRemoteStream(peerConnection, stream => {
        logger.debug('New remote stream', stream);
        remoteStreams.push(stream);
        emit({ eventName: 'remoteStream', args: stream });
      });

      peerConnection.addEventListener('iceconnectionstatechange', args => {
        logger.debug('iceconnectionstatechange viewer', args);
        emit({
          eventName: 'statusUpdate',
          args: { source: 'viewer', info: peerConnection ? peerConnection.iceConnectionState : 'closed' },
        });
      });
      peerConnection.addEventListener('connectionstatechange', args => {
        logger.debug('connectionstatechange viewer', args);
        emit({
          eventName: 'statusUpdate',
          args: { source: 'viewer', info: peerConnection ? peerConnection.iceConnectionState : 'closed' },
        });
      });

      peerConnection.addEventListener('signalingstatechange', event => {
        logger.debug('signalingstatechange', event);
      });
      peerConnection.addEventListener('negotiationneeded', event => {
        logger.debug('negotiationneeded slave', event);
      });
      peerConnection.addEventListener('icegatheringstatechange', event => {
        logger.debug('icegatheringstatechange', event);
      });

      signalingClient.open();
    } catch (e) {
      closeConnection(localStream);
      logger.error(e);
    }

    // the peer renegotiate is needed to prevent idle connection timeout from AWS Kinesis after 5 or 10mins
    const signalingClientPingInterval = setInterval(async () => {
      if (signalingClient && peerConnection) {
        logger.debug('signalingClient ping renegotiate', type, settings);
        peerNegotiate();
      } else {
        logger.debug('No signalingClient or peerConnection to ping', signalingClient, peerConnection);
      }
    }, 1000 * 60 * 4);

    return () => {
      logger.debug('Clean up signalingClientPingInterval');
      clearInterval(signalingClientPingInterval);
    };
  });
}

export function closeConnection(localStream) {
  logger.debug('closeConnection: signalingClient:', signalingClient, 'peerConnection:', peerConnection);

  if (localStream && typeof localStream.release === 'function') {
    localStream.release();
  }

  if (peerConnection) {
    if (localStream) {
      disconnectStreamFromPeerConnection(localStream, peerConnection);
    }
    peerConnection.close();
  }
  if (signalingClient) {
    signalingClient.close();
  }
}

export function closeRemoteStream(stream) {
  logger.debug('Close remote stream');

  if (peerConnection) {
    peerConnection.removeStream(stream);
  }
}

export function toggleCamera(localStream, isEnabled) {
  _forEach(localStream.getVideoTracks(), t => {
    t.enabled = isEnabled;
  });
}

export function toggleMic(localStream, isEnabled) {
  _forEach(localStream.getAudioTracks(), t => {
    t.enabled = isEnabled;
  });
}

export async function toggleScreanSharing(localStream, facingMode, isEnabled, mediaDevices) {
  if (!senderMapping.video) {
    logger.warn('No senderMapping.video to swich to screamSharing');
    return;
  }
  if (isEnabled) {
    try {
      const desktopStream = await mediaDevices.getDisplayMedia();

      // desktopStream.getVideoTracks()[0].onended = () => {
      //   dispatch(socketEmit('desktop_sharing_deactivated', args));
      // };

      // we have the stream for desktop;
      // we can now remove the camera stream and exchange it with the desktop stream
      senderMapping.video.replaceTrack(desktopStream.getVideoTracks()[0]);

      const oldStream = localStream.getVideoTracks()[0];
      localStream.removeTrack(oldStream);

      localStream.addTrack(desktopStream.getVideoTracks()[0]);

      oldStream.stop();
      return true;
    } catch (err) {
      logger.error('Error activating shared screen', err);
      return false;
    }
  } else {
    const userCameraStream = await mediaDevices.getUserMedia({
      ...getUserMediaConstraints('video', { facingMode }),
      audio: false,
    });

    senderMapping.video.replaceTrack(userCameraStream.getVideoTracks()[0]);

    const oldStream = localStream.getVideoTracks()[0];
    localStream.removeTrack(oldStream);

    localStream.addTrack(userCameraStream.getVideoTracks()[0]);

    oldStream.stop();
  }
  return true;
}

export async function changeCameraFacingMode(localStream, mode, mediaDevices) {
  if (localStream.getVideoTracks()[0]._switchCamera) {
    logger.debug('Use native _switchCamera');
    return localStream.getVideoTracks()[0]._switchCamera();
  }
  if (mode === 'user') {
    // we have the user camera stream;
    // we can now remove the environment camera stream and exchange it with the user camera stream
    const userCameraStream = await mediaDevices.getUserMedia({
      ...getUserMediaConstraints('video'),
      audio: false,
    });

    senderMapping.video.replaceTrack(userCameraStream.getVideoTracks()[0]);

    const oldStream = localStream.getVideoTracks()[0];
    localStream.removeTrack(oldStream);

    localStream.addTrack(userCameraStream.getVideoTracks()[0]);

    oldStream.stop();
  } else {
    const environmentCameraStream = await mediaDevices.getUserMedia({
      ...getUserMediaConstraints('video', { facingMode: 'environment' }),
      audio: false,
    });

    // we have the rear camera stream;
    // we can now remove the front camera stream and exchange it with the rear camera stream
    senderMapping.video.replaceTrack(environmentCameraStream.getVideoTracks()[0]);

    const oldStream = localStream.getVideoTracks()[0];
    localStream.removeTrack(oldStream);

    localStream.addTrack(environmentCameraStream.getVideoTracks()[0]);

    oldStream.stop();
  }
}

function connectStreamToPeerConnection(stream, peerConnection) {
  if (peerConnection.addTrack) {
    stream.getTracks().forEach(track => {
      logger.debug('Connect stream track to peerConnection', track);
      senderMapping[track.kind] = peerConnection.addTrack(track, stream);
    });
    logger.debug('Connected tracks ', senderMapping);
  } else {
    logger.debug('Connect stream to peerConnection', stream);
    peerConnection.addStream(stream);
  }
}

function disconnectStreamFromPeerConnection(stream, peerConnection) {
  if (peerConnection.iceConnectionState === 'closed') {
    return;
  }
  if (peerConnection.removeTrack) {
    stream.getTracks().forEach(track => {
      logger.debug('Disconnect stream track from peerConnection', track, peerConnection);
      peerConnection.removeTrack(peerConnection.getSenders().find(sender => sender.track == track));
      senderMapping[track.kind] = null;
    });
  } else {
    logger.debug('Disconnect stream from peerConnection', stream, peerConnection);
    peerConnection.removeStream(stream);
  }
}

function onRemoteStream(peerConnection, onRemoteStream) {
  if (peerConnection.addTrack) {
    // As remote tracks are received, add them to the remote view
    peerConnection.addEventListener('track', event => {
      logger.debug('peerConnection track event', event);
      return onRemoteStream(event.streams[0]);
    });
  } else {
    peerConnection.addEventListener('addstream', event => {
      logger.debug('peerConnection addstream event', event);
      onRemoteStream(event.stream);
    });
  }
}

function stopStream(stream) {
  if (peerConnection) {
    disconnectStreamFromPeerConnection(stream, peerConnection);
  }
  if (stream.getTracks) {
    stream.getTracks().forEach(track => track.stop());
  } else {
    stream.stop();
  }
}
